import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { CreateGreenhouseData, UpdateGreenhouseData } from 'api/greenhouse';
import {
  PaginationData,
  Greenhouse,
} from 'constants/interface';
import { RootState } from 'state';

const greenhouseEntityAdapter = createEntityAdapter<Greenhouse>();

export interface GreenhousesState extends EntityState<Greenhouse> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: GreenhousesState = greenhouseEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createGreenhouseAction = createAsyncThunk(
  'greenhouses/create',
  async (payload: CreateGreenhouseData) => {
    const greenhouseData = await apiClient.greenhouse.createGreenhouse(payload);
    return greenhouseData;
  },
);

export const updateGreenhouseAction = createAsyncThunk(
  'greenhouses/update',
  async (payload: UpdateGreenhouseData) => {
    const greenhouseData = await apiClient.greenhouse.updateGreenhouse(payload);
    return greenhouseData;
  },
);

export const getGreenhouseAction = createAsyncThunk(
  'greenhouses/get',
  async (payload: {
    greenhouseId: string,
  }) => {
    const { greenhouseId } = payload;
    const response = await apiClient.greenhouse.getGreenhouse(greenhouseId);

    return response;
  },
);

export const getGreenhouseListAction = createAsyncThunk(
  'greenhouses/list',
  async (payload: PaginationData) => {
    const response = await apiClient
      .greenhouse
      .getGreenhouseList(payload);

    return response;
  },
);

export const greenhousesSlice = createSlice({
  name: 'greenhouses',
  initialState,
  reducers: {
    cleanGreenhousesAction: greenhouseEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getGreenhouseListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getGreenhouseListAction.fulfilled, (state, action) => {
      greenhouseEntityAdapter.removeAll(state);
      greenhouseEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getGreenhouseListAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  cleanGreenhousesAction,
} = greenhousesSlice.actions;

// Selectors

export const isLoadingGreenhousesSelector = (state: RootState):
  boolean => state.greenhouses.isLoading;

export const greenhousesEntitiesSelector = (state: RootState):
  Dictionary<Greenhouse> => state.greenhouses.entities;

export const greenhousesIdsSelector = (state: RootState):
  EntityId[] => state.greenhouses.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.greenhouses.remoteTotal;
